(() => {
  let isRegistered = false;

  const hasPriceRangeScope = (element) => (
    Array.isArray(element?._x_dataStack)
      && element._x_dataStack.some((scope) => (
        typeof scope?.formatPrice === 'function'
          && typeof scope?.startDrag === 'function'
      ))
  );

  const ensureAlpineComponent = (element) => {
    if (!window.Alpine || !element || hasPriceRangeScope(element)) {
      return;
    }

    if (element._x_dataStack || element._x_marker) {
      if (window.Alpine.destroyTree) {
        window.Alpine.destroyTree(element);
      }

      delete element._x_dataStack;
      delete element._x_marker;
    }

    if (window.Alpine.initTree) {
      window.Alpine.initTree(element);
    }
  };

  const dispatchPriceRangeChange = (root) => {
    const EventConstructor = window.CustomEvent || globalThis.CustomEvent;

    if (!root || typeof root.dispatchEvent !== 'function' || !EventConstructor) {
      return;
    }

    root.dispatchEvent(new EventConstructor('collection-price-range:changed', { bubbles: true }));
  };

  const createCollectionPriceRange = (config = {}) => ({
    min: Number(config.min ?? 0),
    max: Number(config.max ?? 100),
    step: Number(config.step ?? 1),
    valueMin: Number(config.valueMin ?? 40),
    valueMax: Number(config.valueMax ?? 80),
    activeHandle: null,
    trackInset: 15,
    handleHalf: 12,

    init() {
      if (!Number.isFinite(this.min)) {
        this.min = 0;
      }

      if (!Number.isFinite(this.max)) {
        this.max = this.min + 1;
      }

      if (!Number.isFinite(this.step) || this.step <= 0) {
        this.step = 1;
      }

      if (this.max <= this.min) {
        this.max = this.min + this.step;
      }

      this.valueMin = this.clampValue(this.valueMin);
      this.valueMax = this.clampValue(this.valueMax);

      if (this.valueMin > this.valueMax - this.step) {
        this.valueMin = this.valueMax - this.step;
      }
    },

    clampValue(value) {
      return Math.min(this.max, Math.max(this.min, Number(value)));
    },

    get range() {
      return this.max - this.min;
    },

    get minPercent() {
      return this.range > 0 ? ((this.valueMin - this.min) / this.range) * 100 : 0;
    },

    get maxPercent() {
      return this.range > 0 ? ((this.valueMax - this.min) / this.range) * 100 : 100;
    },

    get minPriceValue() {
      return this.formatParamValue(this.valueMin);
    },

    get maxPriceValue() {
      return this.formatParamValue(this.valueMax);
    },

    get isMinDefault() {
      return this.valueMin <= this.min;
    },

    get isMaxDefault() {
      return this.valueMax >= this.max;
    },

    formatParamValue(value) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) {
        return '';
      }

      return amount.toFixed(2).replace(/\.00$/, '');
    },

    formatPrice(value) {
      const amount = Number(value);

      if (!Number.isFinite(amount)) {
        return '';
      }

      return amount.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      });
    },

    positionAt(percent) {
      const inset = this.trackInset;
      const safePercent = Math.min(100, Math.max(0, percent));
      return `calc(${inset}px + (100% - ${inset * 2}px) * ${safePercent / 100})`;
    },

    handleLeft(percent) {
      const inset = this.trackInset;
      const safePercent = Math.min(100, Math.max(0, percent));
      return `calc(${inset}px + (100% - ${inset * 2}px) * ${safePercent / 100} - ${this.handleHalf}px)`;
    },

    tooltipStyle(percent) {
      const safePercent = Math.min(100, Math.max(0, percent));
      return `left: ${this.positionAt(safePercent)}; transform: translateX(-50%)`;
    },

    get minHandleStyle() {
      return `left: ${this.handleLeft(this.minPercent)}`;
    },

    get maxHandleStyle() {
      return `left: ${this.handleLeft(this.maxPercent)}`;
    },

    get minTooltipStyle() {
      return this.tooltipStyle(this.minPercent);
    },

    get maxTooltipStyle() {
      return this.tooltipStyle(this.maxPercent);
    },

    get fillStyle() {
      return `left: ${this.positionAt(this.minPercent)}; right: ${this.positionAt(100 - this.maxPercent)}`;
    },

    dispatchChange() {
      dispatchPriceRangeChange(this.$root);
    },

    startDrag(handle, event) {
      this.activeHandle = handle;
      event.preventDefault();
    },

    stopDrag() {
      const wasDragging = Boolean(this.activeHandle);
      this.activeHandle = null;

      if (wasDragging) {
        this.dispatchChange();
      }
    },

    onDrag(event) {
      if (!this.activeHandle) {
        return;
      }

      const track = this.$refs.track;
      if (!track) {
        return;
      }

      const pointer = event.touches ? event.touches[0] : event;
      const rect = track.getBoundingClientRect();
      const percent = (pointer.clientX - rect.left) / rect.width;
      const rawValue = this.min + percent * this.range;
      const steppedValue = Math.round(rawValue / this.step) * this.step;
      const nextValue = this.clampValue(steppedValue);

      if (this.activeHandle === 'min') {
        this.valueMin = Math.min(nextValue, this.valueMax - this.step);
      }

      if (this.activeHandle === 'max') {
        this.valueMax = Math.max(nextValue, this.valueMin + this.step);
      }
    },

    onTrackPointer(event) {
      const track = this.$refs.track;
      if (!track) {
        return;
      }

      const previousMin = this.valueMin;
      const previousMax = this.valueMax;
      const pointer = event.touches ? event.touches[0] : event;
      const rect = track.getBoundingClientRect();
      const percent = (pointer.clientX - rect.left) / rect.width;
      const rawValue = this.min + percent * this.range;
      const steppedValue = Math.round(rawValue / this.step) * this.step;
      const nextValue = this.clampValue(steppedValue);
      const distanceToMin = Math.abs(nextValue - this.valueMin);
      const distanceToMax = Math.abs(nextValue - this.valueMax);

      if (distanceToMin <= distanceToMax) {
        this.valueMin = Math.min(nextValue, this.valueMax - this.step);
      } else {
        this.valueMax = Math.max(nextValue, this.valueMin + this.step);
      }

      if (this.valueMin !== previousMin || this.valueMax !== previousMax) {
        this.dispatchChange();
      }
    },

    onHandleKeydown(handle, event) {
      let nextValue = handle === 'min' ? this.valueMin : this.valueMax;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        nextValue -= this.step;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        nextValue += this.step;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowDown' && event.key !== 'ArrowRight' && event.key !== 'ArrowUp') {
        return;
      }

      const previousMin = this.valueMin;
      const previousMax = this.valueMax;

      event.preventDefault();
      nextValue = this.clampValue(nextValue);

      if (handle === 'min') {
        this.valueMin = Math.min(nextValue, this.valueMax - this.step);
      } else {
        this.valueMax = Math.max(nextValue, this.valueMin + this.step);
      }

      if (this.valueMin !== previousMin || this.valueMax !== previousMax) {
        this.dispatchChange();
      }
    },
  });

  const registerCollectionPriceRange = () => {
    if (!window.Alpine || isRegistered) {
      return;
    }

    isRegistered = true;
    window.Alpine.data('collectionPriceRange', createCollectionPriceRange);

    requestAnimationFrame(() => {
      document.querySelectorAll('.collection-filter__price-range').forEach((element) => {
        ensureAlpineComponent(element);
      });
    });
  };

  window.JustFishyCollectionPriceRange = {
    createCollectionPriceRange,
  };

  document.addEventListener('alpine:init', registerCollectionPriceRange);
  registerCollectionPriceRange();
})();
